"use client";

import type { ConversationStreamEvent } from "@repo/api/modules/channels/types";
import { EventSourceParserStream } from "eventsource-parser/stream";
import { useEffect, useEffectEvent } from "react";

const RETRY_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

/**
 * Listens to `GET /api/public/:slug/conversations/:id/events` while a conversation is open:
 * staff replies and the end of a handoff. Read with fetch, not EventSource, because the
 * session travels in a header. Every (re)connect opens with `ready` and the current handoff
 * state; a reconnect after a drop also reloads the conversation, since pub/sub keeps no
 * backlog.
 */
export function useConversationEvents({
	conversationId,
	request,
	onEvent,
	onReconnect,
}: {
	conversationId: string | null;
	request: (path: string, init?: RequestInit) => Promise<Response>;
	onEvent: (event: ConversationStreamEvent) => void;
	onReconnect: () => void;
}) {
	const handle = useEffectEvent(onEvent);
	const resync = useEffectEvent(onReconnect);

	useEffect(() => {
		if (!conversationId) {
			return;
		}
		const controller = new AbortController();
		const { signal } = controller;
		let attempt = 0;

		const listen = async () => {
			while (!signal.aborted) {
				try {
					const response = await request(
						`/conversations/${conversationId}/events`,
						{ signal, headers: { accept: "text/event-stream" } },
					);
					// Gone or no longer this session's: nothing to come back for.
					if (
						response.status === 401 ||
						response.status === 404 ||
						!response.body
					) {
						return;
					}
					if (response.ok) {
						if (attempt > 0) {
							resync();
						}
						attempt = 0;
						await readEvents(response.body, (event) =>
							handle(event),
						);
					}
				} catch {
					// Dropped or aborted: retried below unless the page moved on.
				}
				if (signal.aborted) {
					return;
				}
				const wait = RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)];
				attempt += 1;
				await new Promise((resolve) => setTimeout(resolve, wait));
			}
		};
		void listen();
		return () => controller.abort();
	}, [conversationId, request]);
}

/** Server-sent events off a fetch body; heartbeats are skipped. */
async function readEvents(
	body: ReadableStream<BufferSource>,
	onEvent: (event: ConversationStreamEvent) => void,
) {
	// A reader, not `for await`: Safari's streams are not async iterable.
	const events = body
		.pipeThrough(new TextDecoderStream())
		.pipeThrough(new EventSourceParserStream())
		.getReader();
	for (;;) {
		const { value, done } = await events.read();
		if (done) {
			return;
		}
		const { event, data } = value;
		if (!event || event === "heartbeat" || !data) {
			continue;
		}
		try {
			const parsed = JSON.parse(data) as Record<string, unknown>;
			onEvent({ ...parsed, type: event } as ConversationStreamEvent);
		} catch {
			// A malformed event is skipped; the next resync covers it.
		}
	}
}
